import { AnyParsedTag, TagType } from "@/lib/dimension-tag-parser";
import {
  AvatarWithInitials,
  BlockProps,
  Card,
  Column,
  Icon,
  IconName,
  Row,
  SubtitleText,
} from "@/shared/ui";
import React from "react";
import { CustomTagsData } from "../MarkdownText/_components/MarkdownComponents";
import { DimensionCard } from "../DimensionCard";
import { useDictionary } from "@/shared/hooks";
import { first, includes, replace } from "lodash";
import { PersonMention } from "../MarkdownText/_components";
import { useAttachmentRead } from "@/shared/hooks/content/useAttachments";
import { DataItem } from "../DataSection";
import { cn } from "@/lib/utils";

interface ReferenceCardProps {
  reference?: AnyParsedTag;
  data?: CustomTagsData;
  detailed?: boolean;
  onClick?: () => void;
}

interface BaseReferenceProps extends BlockProps {
  icon: IconName;
  type: "link" | "modal";
  referenceName: string;
  detailed?: boolean;
}

const BaseReference = (props: BaseReferenceProps) => {
  const {
    icon,
    type,
    referenceName,
    children,
    detailed = true,
    onClick,
  } = props;
  return (
    <Column
      className="cursor-pointer gap-4 py-4 border-b border-grey-200"
      onClick={onClick}
    >
      {!!detailed && (
        <Row className="items-center gap-2">
          <Icon icon={icon} size="xs" color="lightGrey" />
          <SubtitleText className="text-sm leading-[unset] mt-0.5 flex-1">
            {referenceName}
          </SubtitleText>
          <Icon
            size="xs"
            icon={type === "link" ? "ExternalLink" : "Fullscreen"}
            color="blue"
          />
        </Row>
      )}
      {children}
    </Column>
  );
};

export const ReferenceCard = (props: ReferenceCardProps) => {
  const { reference, data, detailed = true, onClick } = props;
  const { getTerm } = useDictionary();
  const { readAttachment } = useAttachmentRead({});

  if (!reference) return null;

  if (reference.type === "DIMENSION") {
    const dimensions = data?.dimensions || [];
    const { dim_id } = reference;
    const dimension = dimensions.find((dim) => dim.id === dim_id);
    const title = getTerm(dim_id, "name") || "";
    const description = getTerm(dim_id, "description") || "";
    const scoreMultiplier = dimension?.group === "opq" ? 10 : 1; // TODO - Do we need this anymore?
    return (
      <BaseReference
        icon="Big5"
        type="modal"
        referenceName="Dimension"
        detailed={detailed}
        onClick={onClick}
      >
        <DimensionCard
          title={title || " "}
          description={description || ""}
          scores={dimension?.peopleScores || []}
          showAverageScore={false}
          showScore
          score={dimension?.score}
          id={dim_id}
          scoreMultiplier={scoreMultiplier}
        />
      </BaseReference>
    );
  }

  if (reference.type === "PERSON") {
    const { person_id } = reference;
    const person = data?.people?.find((p) => p.id === person_id) || {};

    return (
      <BaseReference
        icon="User"
        type="modal"
        referenceName={"Person"}
        detailed={detailed}
      >
        <span className="inline-flex align-bottom mt-2 items-center gap-2 py-[1px] pl-[2px] pr-1 rounded-full text-sm font-medium bg-gray-200 text-gray-700 border border-gray-300 not-prose">
          <AvatarWithInitials
            size="xs"
            avatar={person?.avatar || ""}
            initials={person.initials || person?.name?.charAt(0)}
            className="w-5 h-5"
          />
          <span className="leading-none">{person.name}</span>
        </span>
      </BaseReference>
    );
  }

  if (reference.type === TagType.FILE) {
    const handleClick = async () => {
      await readAttachment(file_id);
    };
    const { file_id } = reference;
    const file = data?.attachments?.find((a) => a.id === file_id);
    return (
      <BaseReference
        icon="File"
        type="link"
        referenceName="File"
        detailed={detailed}
        onClick={handleClick}
      >
        <Row>
          <Column className="gap-2 w-full min-w-0">
            <span className="truncate text-sm font-semibold">
              {file?.file_name}
            </span>
            <SubtitleText className="text-xs">{file?.description}</SubtitleText>
            {!!file?.tags?.length && (
              <div>
                <span className="rounded-full px-2 bg-grey-100 text-xs text-grey-600 min-h-10">
                  {replace(first(file?.tags) || "", "_", " ")}
                </span>
              </div>
            )}
          </Column>
        </Row>
      </BaseReference>
    );
  }

  return (
    <Card>
      <></>
    </Card>
  );
};
